import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiService } from '@/services/api';

const NONE_SENTINEL = '__none__';

interface UserRow {
  id: string;
  name: string;
  email: string;
}

interface UsersResponse {
  users: UserRow[];
}

interface UserSingleSelectProps {
  value: string | null;
  onChange: (userId: string | null) => void;
  disabled?: boolean;
}

export function UserSingleSelect({ value, onChange, disabled }: UserSingleSelectProps) {
  const { t } = useTranslation();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async (): Promise<void> => {
      try {
        setLoading(true);
        const data = await apiService.get<UsersResponse>('/users');
        setUsers(Array.isArray(data.users) ? data.users : []);
      } catch {
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  return (
    <Select
      disabled={disabled || loading}
      value={value ?? NONE_SENTINEL}
      onValueChange={(v: string): void => {
        onChange(v === NONE_SENTINEL ? null : v);
      }}
    >
      <SelectTrigger className="w-full max-w-md bg-card border-border">
        <SelectValue placeholder={loading ? t('common.loading') : t('leads.autoTransfer.defaultSeller')} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE_SENTINEL}>{t('leads.autoTransfer.defaultSellerNone')}</SelectItem>
        {users.map((user) => (
          <SelectItem key={user.id} value={user.id}>
            {`${user.name} (${user.email})`}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
